import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { createFileRoute } from '@tanstack/react-router';
import { Gift02 } from '@untitledui/icons/Gift02';
import { z } from 'zod';

import { ResearcherShell } from '@/components/navigation/researcher-shell';
import { RedeemPromotionForm } from '@/components/promotions/redeem-promotion-form';
import { resolveActiveDashboard } from '@/utils/session/resolve-active-dashboard';

/**
 * TanStack Router JSON-parses search-param values, so `?code=2026` arrives as
 * a number and a plain `z.string()` would `.catch(undefined)` it away. Promo
 * codes can be all-numeric, so coerce the scalar back to a string first.
 */
const codeSearchSchema = z
  .union([z.string(), z.number(), z.boolean()])
  .transform((value) => String(value).trim())
  .refine((value) => value.length > 0)
  .optional()
  // oxlint-disable-next-line unicorn/no-useless-undefined -- zod `.catch` fallback, not a Promise chain
  .catch(undefined);

const redeemSearchSchema = z.object({
  code: codeSearchSchema,
});

export const Route = createFileRoute('/researchers/_authenticated/redeem/')({
  validateSearch: redeemSearchSchema,
  // Resolves the active org/team like every other researcher area: persists an
  // unambiguous single-team case, else redirects to `/researchers/select-team`
  // with a `returnTo` that preserves `?code=`. Multi-team researchers landing
  // cold from a campaign email are the primary path here, not a fallback.
  beforeLoad: async ({ context, location }) =>
    await resolveActiveDashboard({ context, location }),
  component: RedeemPage,
  head: () => ({ meta: [{ title: t`Redeem a promo code` }] }),
});

function RedeemPage() {
  const { user, activeDashboard } = Route.useRouteContext();
  const search = Route.useSearch();

  return (
    <div className="flex h-screen flex-col print:block print:h-auto">
      <ResearcherShell user={user} activeDashboard={activeDashboard}>
        {/* Heavier bottom padding than top, so the auto-centered block lands a
            little above true center, where it reads as centered. `m-auto`
            rather than `justify-center`: auto margins keep the top reachable
            once the viewport is shorter than the block. */}
        <div className="flex h-full flex-col overflow-y-auto px-8 pt-16 pb-48">
          <div className="m-auto flex w-full max-w-2xl flex-col gap-8">
            <div className="flex flex-col gap-3">
              <h1 className="flex items-center gap-3 font-heading text-display-sm font-semibold text-balance text-primary">
                <Gift02
                  className="size-8 shrink-0 text-brand-secondary"
                  aria-hidden="true"
                />
                <Trans>Enter your promo code</Trans>
              </h1>
              <p className="max-w-prose text-md text-tertiary">
                <Trans>
                  Credits land on your team&apos;s recruitment balance and can
                  be spent on any project. A code can be redeemed once per user.
                </Trans>
              </p>
            </div>

            <RedeemPromotionForm
              key={search.code ?? ''}
              userId={user.id}
              teamId={activeDashboard.team ?? undefined}
              initialCode={search.code}
            />
          </div>
        </div>
      </ResearcherShell>
    </div>
  );
}
